import { Box, Button, Card, CardHeader, CircularProgress, Container, IconButton, MenuItem, Stack, Typography, alpha } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useDropzone } from "react-dropzone";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { RentalOrganizationSettings } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import useModelSavingData from "src/hooks/use-model-saving-data";
import { usePrevious } from "src/hooks/use-previous";
import Iconify from "src/components/iconify/iconify";
import TruxTextField from "src/components/inputs/trux-text-field";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { Constants } from "src/constants";

type PageModel = {
  rentalOrganizationSettings?: RentalOrganizationSettings,
}

export default function Page() {
  const settings = useSettingsContext();

  const dataLoader = useCallback(() => ({
    rentalOrganizationSettings: axios.get(endpoints.rentalOrganizationSettings.current).then(response => response.data),
  }), []);

  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const previousRentalOrganizationSettings = usePrevious(data?.rentalOrganizationSettings)

  const rentalOrganizationSettingsModel = useModelSavingData<RentalOrganizationSettings>(
    endpoints.rentalOrganizationSettings.update
  );

  useEffect(() => {
    if (data?.rentalOrganizationSettings && data.rentalOrganizationSettings !== previousRentalOrganizationSettings) {
      rentalOrganizationSettingsModel.set(data.rentalOrganizationSettings)
    }
  }, [previousRentalOrganizationSettings, data?.rentalOrganizationSettings, rentalOrganizationSettingsModel]);



  const [savingFile, setSavingFile] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(file => {
        setSavingFile(true)

        // here we need to upload the image
        axios.postForm(
          endpoints.organizations.uploadFile,
          { file }
        ).then(response => response.data).then((response) => {
          if (response.fileUrl) {
            enqueueSnackbar("Successfully uploaded the File", { variant: "success" });

            rentalOrganizationSettingsModel.update(organization => {
              organization.companyLogoUrl = response.fileUrl;
            });
          }
        }).catch(() => {
          enqueueSnackbar("Your file could not be uploaded. Please try again.", { variant: "error" });
        }).finally(() => {
          setSavingFile(false);
        })
      })
    },
    [rentalOrganizationSettingsModel]
  );

  const onRemoveFile = useCallback(() => {
    rentalOrganizationSettingsModel.update(organization => {
      organization.companyLogoUrl = "";
    })
  }, [rentalOrganizationSettingsModel]);

  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop, accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
    }
  })



  const [isOpenSaveChanges, setIsOpenSaveChanges] = useState(false);

  const updateRentalOrganizationSettings = useCallback(() => {
    rentalOrganizationSettingsModel.save().then((response) => {
      if (response) {
        // we should apply the response back to the load model
        updateData("rentalOrganizationSettings")(response.data as any)

        enqueueSnackbar("Successfully updated the Rental Organization Settings", { variant: "success" })
        setIsOpenSaveChanges(false);
      }
    })
  }, [rentalOrganizationSettingsModel, updateData]);


  const updateRentalOrganizationSettingsButton = <Button
    onClick={() => setIsOpenSaveChanges(true)}
    variant="outlined"
    color="info"
    startIcon={<Iconify icon="basil:save-solid" />}
  >
    Save Changes
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {rentalOrganizationSettingsModel.model && <Box sx={{ display: "flex", flexGrow: 1 }}>

          <Stack direction="column" flexGrow={1} gap={2}>
            <PageHeader title="Settings">
              {updateRentalOrganizationSettingsButton}
            </PageHeader>

            <Card>
              <CardHeader title="General" />

              <Stack spacing={2} sx={{ p: 2 }}>
                <TruxFormField label="Company Name *">
                  <TruxTextField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="companyName"
                  />
                </TruxFormField>

                <TruxFormField label="Pre Image Text">
                  <TruxTextField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="preImageText"
                  />
                </TruxFormField>

                <TruxFormField label="Post Image Text">
                  <TruxTextField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="postImageText"
                  />
                </TruxFormField>

                <TruxFormField label="Company Address">
                  <Stack spacing={1}>
                    <Box display="flex" gap={2}>
                      <Box flexGrow={1}>
                        <TruxSimpleTextField label="Street 1" value={rentalOrganizationSettingsModel.model.companyAddress.street1} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.street1 = val;
                        })} />
                      </Box>
                      <Box flexGrow={1}>
                        <TruxSimpleTextField label="Street 2" value={rentalOrganizationSettingsModel.model.companyAddress.street2} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.street2 = val;
                        })} />
                      </Box>
                    </Box>
                    <Box display="flex" gap={2}>
                      <Box flexGrow={1}>
                        <TruxSimpleTextField label="City" value={rentalOrganizationSettingsModel.model.companyAddress.city} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.city = val;
                        })} />
                      </Box>
                      <Box flexGrow={1}>
                        <TruxSimpleSelectField value={rentalOrganizationSettingsModel.model.companyAddress.state} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.state = val;
                        })}>
                          <MenuItem value="" selected={!!rentalOrganizationSettingsModel.model.companyAddress.state}>Please Select</MenuItem>
                          {Constants.STATES.map((state) => <MenuItem value={state} key={state} selected={state === rentalOrganizationSettingsModel?.model?.companyAddress.state}>{state}</MenuItem>)}
                        </TruxSimpleSelectField>
                      </Box>
                      <Box flexGrow={1}>
                        <TruxSimpleTextField label="Zip" value={rentalOrganizationSettingsModel.model.companyAddress.zip} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.zip = val;
                        })} />
                      </Box>
                    </Box>
                  </Stack>
                </TruxFormField>


                <TruxFormField label="Company Image">
                  {rentalOrganizationSettingsModel.model && <>
                    {savingFile && <CircularProgress />}
                    {!savingFile && rentalOrganizationSettingsModel.model.companyLogoUrl && <Stack direction="row" gap={2}>
                      <Box maxWidth="500px">
                        <img src={rentalOrganizationSettingsModel.model.companyLogoUrl} alt="Invoice Logo" />
                      </Box>
                      <Box>
                        <IconButton color="error" onClick={onRemoveFile} >
                          <Iconify icon="solar:trash-bin-trash-bold" />
                        </IconButton>
                      </Box>
                    </Stack>}
                    {!savingFile && !rentalOrganizationSettingsModel.model.companyLogoUrl && <Box>
                      <Box {...getRootProps()} onFocus={null as any} onBlur={null as any}>
                        <input {...getInputProps()} />

                        <Box sx={{
                          m: 0.5,
                          flexShrink: 0,
                          flexGrow: 1,
                          height: "80px",
                          width: "120px",
                          borderRadius: 1,
                          cursor: 'pointer',
                          alignItems: 'center',
                          color: 'text.disabled',
                          justifyContent: 'center',
                          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                          border: (theme) => `dashed 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                          ...(isDragActive && {
                            opacity: 0.72,
                          }),
                          '&:hover': {
                            opacity: 0.72,
                          }
                        }}><Stack spacing={0.5} paddingTop={1} alignItems="center">
                            <Iconify icon="eva:cloud-upload-fill" width={40} />
                            <Typography variant="body2">Upload File</Typography>
                          </Stack></Box>
                      </Box>
                    </Box>}
                  </>}
                </TruxFormField>

              </Stack>
            </Card>
          </Stack>
        </Box>}
      </DisplayPageData >

      {
        rentalOrganizationSettingsModel.model &&
        <SimpleDialog
          title="Save Rental Organization Settings"
          open={isOpenSaveChanges}
          onCancel={() => {
            setIsOpenSaveChanges(false);
            rentalOrganizationSettingsModel.clearErrors();
          }}
          onSubmit={updateRentalOrganizationSettings}
          submitDisabled={rentalOrganizationSettingsModel.loading}
          submitButton="Save Organization"
          submitButtonIcon={<Iconify icon="basil:save-solid" />}
          submitButtonColor="info">

          <DisplayErrors savingData={rentalOrganizationSettingsModel} />

          Are you sure you would like to save your changes?
        </SimpleDialog>
      }
    </Container >
  );
}
